import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { BookOpen, Video, Save, Loader2, PlayCircle } from "lucide-react";

function toEmbed(url) {
  if (!url) return null;
  const u = url.trim();
  let m = u.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([\w-]{11})/);
  if (m) return { type: "iframe", src: `https://www.youtube.com/embed/${m[1]}` };
  m = u.match(/loom\.com\/(?:share|embed)\/([\w-]+)/);
  if (m) return { type: "iframe", src: `https://www.loom.com/embed/${m[1]}` };
  if (/\.(mp4|webm|mov)(\?.*)?$/i.test(u)) return { type: "video", src: u };
  return { type: "link", src: u };
}

const GUIDE = [
  { t: "1. Antre nan Panèl Admin", b: ["Louvri app la → Settings → peze \"Ouvri Panèl Admin\".", "Mete PIN sekirite a (default 3755) pou w antre."] },
  { t: "2. Kreye / Jere Manm (Grant Access)", b: ["Chèche yon manm pa imèl nan \"Manage Access\".", "Ou ka chanje wòl li, re-voye enfo koneksyon, voye dokiman, oswa efase kont lan nèt (Zòn Danje).", "Efase yon kont si imèl la double pou w ka reyitilize l."] },
  { t: "3. Peman", b: ["Nan tab Payments, apwouve peman kripto/WhatsApp ki annatant.", "Peman kat Stripe yo aktive otomatik.", "Bouton \"Rezèt Done Tès\" mete revni a $0 anvan lansman (li efase pèman tès yo, PA kont yo)."] },
  { t: "4. Sipò Ajan (Chat Kliyan)", b: ["Nan tab \"Sipò Ajan\", w ap wè chak kliyan ki mande yon ajan.", "Peze konvèsasyon an epi reponn — repons ou parèt dirèkteman nan chat kliyan an.", "Chat yo disparèt nan bò kliyan apre 30 jou, men ou kenbe achiv la la a."] },
  { t: "5. Mentor & Patnè", b: ["Nan tab Mentors/Patnè, revize aplikasyon yo.", "Chak aplikan telechaje 3 dokiman: ID Fas, ID Do, Foto Kont Reyèl — peze pou wè yo.", "Lè w apwouve, sistèm lan voye enfo koneksyon + lyen referral otomatik pa imèl."] },
  { t: "6. Lyen Referral", b: ["Chak patnè/mentor gen pwòp lyen: trzefxpro.com/r/{username}.", "Lè yon kliyan peye atravè lyen an, moun sa a touche komisyon otomatik."] },
  { t: "7. Voye Dokiman pa Imèl", b: ["Nan Grant Access, sèvi ak \"Voye Dokiman pa Imèl\" pou voye fichye bay nenpòt moun.", "Chèche yon manm epi peze \"Voye Dokiman\" pou ranpli imèl la otomatik."] },
];

export function GuideTab({ canEdit }) {
  const [url, setUrl] = useState("");
  const [draft, setDraft] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/admin/guide-video").then(({ data }) => { setUrl(data.url || ""); setDraft(data.url || ""); }).catch(() => {});
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.post("/admin/guide-video", { url: draft });
      setUrl(data.url);
      toast.success("Videyo gid la anrejistre");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const embed = toEmbed(url);

  return (
    <div className="space-y-5" data-testid="guide-tab">
      {/* Video */}
      <div className="border border-[#222] bg-[#0F0F0F] p-5">
        <div className="flex items-center gap-2 mb-3">
          <Video className="h-4 w-4 text-[#00F593]" />
          <h3 className="font-heading font-semibold text-sm">Videyo Gid</h3>
        </div>
        {embed && embed.type === "iframe" && (
          <div className="relative w-full rounded-lg overflow-hidden bg-black" style={{ paddingTop: "56.25%" }} data-testid="guide-video-frame">
            <iframe src={embed.src} title="Guide" allow="fullscreen" allowFullScreen className="absolute inset-0 w-full h-full" />
          </div>
        )}
        {embed && embed.type === "video" && (
          <video src={embed.src} controls className="w-full rounded-lg bg-black" data-testid="guide-video-file" />
        )}
        {(!embed || embed.type === "link") && (
          <div className="rounded-lg border border-dashed border-[#333] bg-[#0A0A0A] py-10 flex flex-col items-center justify-center text-center px-4" data-testid="guide-video-empty">
            <PlayCircle className="h-10 w-10 text-[#333] mb-2" />
            <p className="text-sm text-[#888]">{embed ? "Lyen an pa yon videyo YouTube/Loom validе." : "Poko gen videyo. Kole yon lyen YouTube oswa Loom anba a."}</p>
            {embed && embed.type === "link" && <a href={embed.src} target="_blank" rel="noreferrer" className="text-[#1C8CFF] text-xs mt-2 underline">Louvri lyen an</a>}
          </div>
        )}
        {canEdit && (
          <div className="flex flex-col sm:flex-row gap-2 mt-3">
            <Input data-testid="guide-video-input" value={draft} onChange={(e) => setDraft(e.target.value)}
              placeholder="Kole lyen YouTube oswa Loom ou (egz. https://youtu.be/…)" className="bg-[#0A0A0A] border-[#222] rounded-sm h-11 flex-1" />
            <Button onClick={save} disabled={saving} data-testid="guide-video-save" className="h-11 rounded-sm bg-[#00F593] text-black hover:bg-[#00d97f] px-5">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Save className="mr-1.5 h-4 w-4" /> Anrejistre</>}
            </Button>
          </div>
        )}
      </div>

      {/* Written guide */}
      <div className="border border-[#222] bg-[#0F0F0F] p-5">
        <div className="flex items-center gap-2 mb-4">
          <BookOpen className="h-4 w-4 text-[#1C8CFF]" />
          <h3 className="font-heading font-semibold text-sm">Gid Etap-pa-Etap</h3>
        </div>
        <div className="space-y-4">
          {GUIDE.map((s, i) => (
            <div key={i} className="border-l-2 border-[#1C8CFF]/40 pl-4" data-testid={`guide-step-${i}`}>
              <div className="font-semibold text-sm text-white mb-1.5">{s.t}</div>
              <ul className="space-y-1">
                {s.b.map((line, j) => (
                  <li key={j} className="text-[13px] text-[#9db5d8] leading-relaxed flex gap-2">
                    <span className="text-[#1C8CFF] shrink-0">›</span><span>{line}</span>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
